import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/primitives";
import { getCatalogue, refreshCatalogue } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sprout, Search, RefreshCw, ExternalLink, Loader2, Flower2 } from "lucide-react";
import { toast } from "sonner";

export default function Catalogue() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [data, setData] = useState({ items: [], fetched_at: null, count: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const d = await getCatalogue();
      setData(d);
    } catch {
      toast.error("Couldn't load the catalogue. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getCatalogue();
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) toast.error("Couldn't load the catalogue. Try again in a moment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((v) =>
      [v.name, v.colour, v.bloom_size, v.height, v.description].some((s) => (s || "").toLowerCase().includes(q))
    );
  }, [data.items, query]);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await refreshCatalogue();
      toast.success(`Refreshed — ${r.count} varieties from Heritage Dahlias`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reach Heritage Dahlias just now.");
    } finally {
      setRefreshing(false);
    }
  };

  const addToGarden = (v) => {
    nav("/garden/new", {
      state: {
        prefill: {
          name: v.name || "",
          category: "dahlia",
          type: "tuber",
          variety: v.bloom_size || "",
          color: v.colour || "",
          vendor: "Heritage Dahlias",
          notes: v.description || "",
          photos: v.image ? [v.image] : [],
        },
      },
    });
  };

  return (
    <div data-testid="catalogue-page">
      <PageHeader
        kicker="From the Patch"
        title="The Catalogue"
        subtitle="Heirloom varieties from Heritage Dahlias, Aotearoa"
      />

      {/* Top bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between mt-2 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
          <input
            className="field pl-10"
            placeholder="Search by name, colour, size…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="catalogue-search"
          />
        </div>
        <div className="flex gap-2">
          <a
            href="https://heritagedahlias.co.nz/dahlias/catalog"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
            data-testid="catalogue-source-link"
          >
            <ExternalLink size={14} /> Visit Heritage
          </a>
          {isAdmin && (
            <button
              type="button"
              onClick={doRefresh}
              disabled={refreshing}
              className="btn-ghost"
              data-testid="catalogue-refresh"
            >
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {refreshing ? "Refreshing…" : "Refresh now"}
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-16 text-ink-soft" data-testid="catalogue-loading">
          <Loader2 size={28} className="animate-spin mx-auto mb-3 text-sage-dark" />
          Gathering varieties from the shed…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState query={query} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="catalogue-grid">
          {filtered.map((v) => (
            <VarietyCard key={v.id} v={v} onAdd={() => addToGarden(v)} />
          ))}
        </div>
      )}

      {/* Footer meta */}
      {data.fetched_at && (
        <p className="text-xs text-ink-soft text-center mt-10" data-testid="catalogue-meta">
          Synced from <a className="underline" href="https://heritagedahlias.co.nz/dahlias/catalog" target="_blank" rel="noopener noreferrer">heritagedahlias.co.nz</a> · {prettyTime(data.fetched_at)}
        </p>
      )}
    </div>
  );
}

function VarietyCard({ v, onAdd }) {
  const img = v.tile_image || v.image;
  return (
    <article className="h-card overflow-hidden flex flex-col group transition-shadow hover:shadow-seal" data-testid={`variety-${v.id}`}>
      <div className="aspect-[4/5] bg-cream-deep overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={v.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sage-dark">
            <Flower2 size={48} />
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-2xl text-sage-dark leading-tight" data-testid={`variety-name-${v.id}`}>
          {v.name}
        </h3>
        {(v.colour || v.bloom_size) && (
          <p className="font-display italic text-ink-soft mt-1 text-sm">
            {[v.colour, v.bloom_size].filter(Boolean).join(" · ")}
          </p>
        )}
        {v.height && (
          <p className="text-xs text-ink-soft mt-1">Height · {v.height}</p>
        )}
        {v.description && (
          <p className="text-sm text-ink mt-3 leading-relaxed line-clamp-4">{v.description}</p>
        )}
        <button
          type="button"
          onClick={onAdd}
          className="btn-heritage mt-5 w-full"
          data-testid={`variety-add-${v.id}`}
        >
          <Sprout size={15} /> Add to my Garden
        </button>
      </div>
    </article>
  );
}

function EmptyState({ query }) {
  return (
    <div className="h-card text-center py-12 px-6" data-testid="catalogue-empty">
      <Flower2 size={36} className="text-sage-dark mx-auto" />
      <p className="font-display italic text-ink-soft mt-3 text-lg">
        {query ? "Nothing matches that search just yet." : "The catalogue is still being gathered…"}
      </p>
    </div>
  );
}

function prettyTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.round(hrs / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  } catch {
    return "";
  }
}
